import { ChecklistActions, ChecklistActionTypes, TourActions, TourActionTypes } from '../actions';
import { attachFuelRefill, attachNewMachine, attachNewRasterLocation, attachUpdates, getTSAIdsForContractDetailId, isYesterdayOnlyTour, recompileFinalizedStatuses, setUnloadAddresses, sortNestedTourData } from '@shared/*';
import { Depot, Driver, DriverName, DriverNote, Machine, StatusType, Tour } from '@models/*';
import { SwapProposal } from '../../../interfaces/switch-machine';

export interface TourState {
    entities: { [id: number]: Tour };
    depots: { [key: number]: Depot };
    driverNotes: { [key: string]: DriverNote[] };
    driverNames: { [key: string]: DriverName };
    drivers: { [key: string]: Driver };
    // todo: move driver id to core (consider also with everything else from get-driver-data)
    driverId: number | null;
    loaded: boolean;
    loading: boolean;
    singleLoaded: number[];
    /**
     * @deprecated Probably no longer used.
     */
    singleLoading: number[];
    actionsUpdating: number[];
    /**
     * @deprecated Only used to disable the save button in the switch swap component.
     */
    machineSearching: boolean;
    foundMachines: { [fleetNumber: string]: Machine } | null;
    isSwapProposal: boolean;
    swapProposal: SwapProposal;
    filterOn: boolean;
    notesForCounterParty: { [tourStopActionId: number]: string };
}

const initState: TourState = {
    entities: {},
    depots: {},
    driverNotes: {},
    driverNames: {},
    drivers: {},
    // todo: move driver id to core (consider also with everything else from get-driver-data)
    driverId: null,
    loaded: false,
    loading: false,
    singleLoaded: [],
    singleLoading: [],
    actionsUpdating: [],
    machineSearching: false,
    foundMachines: null,
    isSwapProposal: false,
    swapProposal: null,
    filterOn: false,
    notesForCounterParty: {},
};

export function tourReducer(
    state = initState,
    action: TourActions | ChecklistActions
): TourState {

    switch (action.type) {
        /*
         * for LOAD_DRIVER_DATA the original LOAD_TOURS functionality is preserved,
         * while gradually upgrading to add normalized data with separate collections
         * e.g. starting with "driver notes" for the current tours
         */
        case TourActionTypes.LOAD_DRIVER_DATA: {
            return {
                ...state,
                loading: true,
            };
        }

        /*
         * TODO: this was originally based on "load tours" which returns the tours with all nested data
         *  which wasn't normalized into separate collections to de-duplicate data such as transports, driver notes
         *  this will make it easier to update things like machine locations without having to search the full state in memory
         *  so the intention is to gradually upgrade the shape of the state in NgRx memory, and reducers, selectors, actions accordingly
         */
        case TourActionTypes.LOAD_DRIVER_DATA_SUCCESS: {
            let processedTours;
            const { depots, driverId, driverNames, driverNotes, drivers, tours } = action.payload;
            if (tours && tours.length) {
                processedTours = tours
                    .map(tour => setUnloadAddresses(tour))
                    .map(tour => sortNestedTourData(tour))
                    .filter(tour => tour.status !== StatusType.CANCELLED)
                    .filter(tour => !(isYesterdayOnlyTour(tour)
                        && (tour.status !== StatusType.NEW)
                        && (tour.status !== StatusType.INPROGRESS)));
            } else {
                processedTours = [];
            }

            const entities = processedTours.reduce((accum: { [id: number]: Tour }, elem: Tour) => {
                return {
                    ...accum,
                    [elem.id]: elem,
                };
            }, {});

            return {
                ...state,
                loading: false,
                loaded: true,
                entities,
                depots,
                driverId,
                driverNames,
                driverNotes,
                drivers,
            };
        }

        case TourActionTypes.LOAD_DRIVER_DATA_FAIL: {
            return {
                ...state,
                loading: false,
                loaded: false,
            };
        }

        case TourActionTypes.LOAD_TOUR_SINGLE: {
            return {
                ...state,
                singleLoaded: [...state.singleLoaded.filter(id => id !== action.payload)],
                singleLoading: [...state.singleLoading, action.payload]
            };
        }

        case TourActionTypes.LOAD_TOUR_SINGLE_SUCCESS: {
            const newTour = action.payload;
            let entities;

            if (newTour && newTour.status !== StatusType.CANCELLED) {
                const { [action.payload.id]: removedTour, ...restTourEntities } = state.entities;
                entities = {
                    ...restTourEntities,
                    [action.payload.id]: setUnloadAddresses(sortNestedTourData(action.payload)),
                };
            } else {
                entities = state.entities;
            }

            return {
                ...state,
                singleLoaded: [...state.singleLoaded, action.payload.id],
                singleLoading: [...state.singleLoading.filter(id => id !== action.payload.id)],
                entities
            };
        }

        case TourActionTypes.LOAD_TOUR_SINGLE_FAIL: {
            let singleLoaded: number[];

            if (state.entities[action.payload.id]) {
                singleLoaded = [...state.singleLoaded, action.payload.id];
            } else {
                singleLoaded = state.singleLoaded;
            }

            return {
                ...state,
                singleLoaded,
                singleLoading: [...state.singleLoading.filter(id => id !== action.payload.id)]
            };
        }

        case TourActionTypes.COMPLETE_TOUR_STOP_ACTIONS: {
            const updatingActionIds = action.payload.updateData.map(elem => elem.tourStopActionId);

            return {
                ...state,
                actionsUpdating: [...state.actionsUpdating, ...updatingActionIds]
            };
        }

        case TourActionTypes.FUEL_REFILL:
        case ChecklistActionTypes.SUBMIT_PROTOCOL: {
            return {
                ...state,
                actionsUpdating: [...state.actionsUpdating, action.payload.tourStopActionId]
            };
        }

        case TourActionTypes.FUEL_REFILL_SUCCESS: {
            const entities: { [id: number]: Tour } = attachFuelRefill(state.entities, action.payload);

            return {
                ...state,
                actionsUpdating: [...state.actionsUpdating.filter(id => id !== action.payload.tourStopActionId)],
                entities
            };
        }

        case ChecklistActionTypes.SUBMIT_PROTOCOL_SUCCESS: {
            return {
                ...state,
                notesForCounterParty: {
                    [action.payload.tourStopActionId]: null,
                }
            };
        }

        case TourActionTypes.SWITCH_MACHINE: {
            const actionsUpdating = getTSAIdsForContractDetailId(Object.values(state.entities), action.payload.contractDetailId);
            return {
                ...state,
                actionsUpdating: [...state.actionsUpdating, ...actionsUpdating],
                isSwapProposal: false,
                swapProposal: null
            };
        }

        case TourActionTypes.SWITCH_MACHINE_SUCCESS: {
            const updatedActions = getTSAIdsForContractDetailId(Object.values(state.entities), action.payload.contractDetailId);

            // check for swapProposal
            if (action.payload.swapProposal) {
                return {
                    ...state,
                    actionsUpdating: [...state.actionsUpdating.filter(id => !updatedActions.includes(id))],
                    isSwapProposal: true,
                    swapProposal: action.payload.swapProposal
                };
            } else {
                const updateDetails = {
                    newMachine: state.foundMachines[action.payload.newFleetNumber],
                    oldMachineInternalId: action.payload.oldMachineInternalId,
                };
                const entities: { [id: number]: Tour } = attachNewMachine(state.entities, updateDetails);
                return {
                    ...state,
                    actionsUpdating: [...state.actionsUpdating.filter(id => !updatedActions.includes(id))],
                    entities,
                    isSwapProposal: false,
                    swapProposal: null
                };
            }
        }

        case  TourActionTypes.SWITCH_MACHINE_FAIL: {
            const updatedActions = getTSAIdsForContractDetailId(Object.values(state.entities), action.payload.contractDetailId);

            return {
                ...state,
                actionsUpdating: [...state.actionsUpdating.filter(id => !updatedActions.includes(id))],
                isSwapProposal: false,
                swapProposal: null
            };
        }

        case  TourActionTypes.CANCEL_SWITCH_MACHINE: {
            return {
                ...state,
                foundMachines: null,
                isSwapProposal: false,
                swapProposal: null
            };
        }

        case  TourActionTypes.CANCEL_SWITCH_MACHINE_UPDATING: {
            const updatedActions = getTSAIdsForContractDetailId(Object.values(state.entities), action.payload.contractDetailId);

            return {
                ...state,
                actionsUpdating: [...state.actionsUpdating.filter(id => !updatedActions.includes(id))],
            };
        }

        case TourActionTypes.FUEL_REFILL_FAIL:
        case ChecklistActionTypes.SUBMIT_PROTOCOL_FAIL: {
            return {
                ...state,
                actionsUpdating: [...state.actionsUpdating.filter(id => id !== action.payload.tourStopActionId)]
            };
        }

        case TourActionTypes.SEARCH_MACHINE: {
            return {
                ...state,
                machineSearching: true
            };
        }

        case TourActionTypes.SEARCH_MACHINE_SUCCESS: {
            const searchedMachines = {
                ...state.foundMachines,
                [action.payload.fleetNumber]: action.payload
            };

            return {
                ...state,
                machineSearching: false,
                foundMachines: searchedMachines
            };
        }

        case TourActionTypes.SEARCH_MACHINE_FAIL: {
            return {
                ...state,
                machineSearching: false
            };
        }

        case TourActionTypes.UNLOAD_MACHINES_AT_RASTER: {
            const updatingActionIds = action.payload.map(elem => elem.tourStopActionId);

            return {
                ...state,
                actionsUpdating: [...state.actionsUpdating, ...updatingActionIds]
            };
        }

        case TourActionTypes.ADD_REQUIRED_TRACKED_ACTIONS_FAIL: {
            return {
                ...state,
                actionsUpdating: [...state.actionsUpdating.filter(id => !action.payload.tourStopActionIds.includes(id))]
            };
        }

        case TourActionTypes.ADD_REQUIRED_TRACKED_ACTIONS: {
            const updatingActionIds = action.payload.map(elem => elem.tourStopActionId);

            const newTourEntities: { [id: number]: Tour } = attachUpdates(state.entities, action.payload);
            const entities = recompileFinalizedStatuses(newTourEntities);

            return {
                ...state,
                actionsUpdating: [...state.actionsUpdating.filter(id => !updatingActionIds.includes(id))],
                entities
            };
        }

        case TourActionTypes.UPDATE_RASTER_LOCATION: {
            const entities = attachNewRasterLocation(state.entities, action.payload);

            return {
                ...state,
                entities
            };
        }

        case TourActionTypes.TOGGLE_TOURS_FILTER: {
            return {
                ...state,
                filterOn: !state.filterOn
            };
        }

        case TourActionTypes.UPSERT_DRIVER_NOTE:
        case TourActionTypes.UPSERT_DRIVER_NOTE_FAIL: {
            return {
                ...state,
            };
        }

        case TourActionTypes.UPSERT_DRIVER_NOTE_SUCCESS: {
            const { driverNote: upsertedNote } = action.payload;
            const key = upsertedNote.compositeKey;
            const driverNotes = state.driverNotes;
            if (!driverNotes[key]) {
                return state;
            }
            if (driverNotes[key].some(note =>
                note.driverId === upsertedNote.driverId &&
                (!upsertedNote.driverNameId || upsertedNote.driverNameId === note.driverNameId)
            )) {
                return {
                    ...state,
                    driverNotes: {
                        ...driverNotes,
                        [key]: driverNotes[key].map(note =>
                            note.driverId === upsertedNote.driverId &&
                            (!upsertedNote.driverNameId || upsertedNote.driverNameId === note.driverNameId)
                                ? upsertedNote
                                : note
                        ),
                    },
                };
            }
            return {
                ...state,
                driverNotes: {
                    ...driverNotes,
                    [key]: [...driverNotes[key], upsertedNote],
                },
            };
        }

        case TourActionTypes.DELETE_DRIVER_NOTE:
        case TourActionTypes.DELETE_DRIVER_NOTE_FAIL: {
            return {
                ...state,
            };
        }

        case TourActionTypes.DELETE_DRIVER_NOTE_SUCCESS: {
            const { driverNote: deletedNote } = action.payload;
            const { compositeKey, driverId, driverNameId } = deletedNote;
            return {
                ...state,
                driverNotes: {
                    ...state.driverNotes,
                    [compositeKey]: state.driverNotes[compositeKey].filter(driverNote => {
                        return !driverNote.driverNameId
                            ? driverNote.driverId !== driverId
                            : driverNote.driverId !== driverId || driverNote.driverNameId !== driverNameId;
                    }),
                },
            };
        }

        case TourActionTypes.UPSERT_DRIVER_MESSAGE_FOR_COUNTER_PARTY: {
            const { tourStopActionId, note } = action.payload;
            return {
                ...state,
                notesForCounterParty: {
                    ...state.driverNotes,
                    [tourStopActionId]: note,
                },
            };
        }

        case TourActionTypes.DELETE_DRIVER_MESSAGE_FOR_COUNTER_PARTY: {
            const { tourStopActionId } = action.payload;
            return {
                ...state,
                notesForCounterParty: {
                    ...state.driverNotes,
                    [tourStopActionId]: null,
                },
            };
        }
    }

    return state;
}
